/**
 * Home's cards, one per export.
 *
 * SPLIT OUT OF `Home.tsx` in Pass 14. Home is a composition — which cards, in which order, at
 * which rank, for this phase and this role — and it was 1,100 lines because every card's markup
 * was inline underneath that decision. The composition is now readable in one screen and each
 * card is a thing you can find by name.
 *
 * Nothing here fetches. Every card takes what it draws, which is what lets Home stay the only
 * place that knows how to read the store.
 */

import { type ReactNode, useEffect, useState } from 'react';
import { currentManagers, managerShort } from '../domain/identity.js';
import { bothZones, liveLeft, type Urgency } from '../domain/deadlines.js';
import type { DeadlineRow } from '../domain/deadlineTable.js';
import type { HomeTask, TrackRow } from '../domain/homePlan.js';
import type { WireLine } from '../domain/wire.js';
import { Citations } from './RuleText.js';
import { GmBadge } from './GmBadge.js';
import { dock } from './dock.js';
import { useSheetSwipe } from './useSheetSwipe.js';

/* ------------------------------------------------------------------------------------ the wire */

/**
 * What the league has just done, one line at a time.
 *
 * EIGHT SECONDS, AND IT STOPS WHEN TOUCHED. A ticker is a promise that nothing on it matters
 * enough to keep still for, so it may not carry anything a manager has to act on — this is the
 * league's news, and every task he owes is on the page underneath it. Pausing on hover and on
 * touch is what makes it readable anyway: the one line somebody wants is the one their finger is
 * already on.
 *
 * `prefers-reduced-motion` stops the rotation entirely rather than shortening it. Somebody who has
 * asked their phone to stop moving things has asked for this too, and the newest line is the one
 * that would have been shown first.
 */
export function Wire(
  { lines, feed, onGo }: {
    /** The ticker's lines: the first fifteen of `feed`. */
    lines: readonly WireLine[];
    /** The whole feed the sheet shows, newest first. */
    feed: readonly WireLine[];
    onGo: (s: string, p: string) => void;
  },
) {
  const [at, setAt] = useState(0);
  const [held, setHeld] = useState(false);
  const [open, setOpen] = useState(false);
  const swipe = useSheetSwipe(() => setOpen(false));

  useEffect(() => {
    if (held || open || lines.length < 2) return undefined;
    const still = typeof window !== 'undefined'
      && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (still) return undefined;
    const t = setInterval(() => setAt((i) => (i + 1) % lines.length), 8000);
    return () => clearInterval(t);
  }, [held, open, lines.length]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  if (lines.length === 0) return null;
  const line = lines[at % lines.length]!;

  return (
    <div
      className="wire"
      onMouseEnter={() => setHeld(true)}
      onMouseLeave={() => setHeld(false)}
      onTouchStart={() => setHeld(true)}
      onTouchEnd={() => setHeld(false)}
    >
      {/*
        TAPPING THE TICKER OPENS THE FEED, NOT THE RECORD (Tim, 3 September). A line that rolls
        every eight seconds is a poor thing to aim at; the sheet holds still, shows the fifty most
        recent, and each of THOSE is the door to its record.
      */}
      <button
        type="button"
        className="wireline"
        /* Keyed by index so the line remounts and replays its own arrival. */
        key={at}
        onClick={() => setOpen(true)}
      >
        <span className="wiredot" aria-hidden="true" />
        <span className="wiretext">{line.text}</span>
        <span className="wirewhen">{wireWhen(line.at)}</span>
      </button>
      <button type="button" className="wireall" onClick={() => setOpen(true)}>
        THE HATER WIRE
      </button>

      {open && dock(
        <>
          <button type="button" className="sheetscrim kdscrim" aria-label="Close the wire"
            onClick={() => setOpen(false)} />
          <div className="kdsheet wiresheet" role="dialog" aria-modal="true" aria-label="The Hater Wire"
            style={swipe.style} {...swipe.handlers}>
            <span className="sheetgrip" aria-hidden="true" />
            <div className="wiresheethead">
              <h2 className="kdsheeth">The Hater Wire</h2>
              <span className="wiresheetcount">last {feed.length}</span>
              <button type="button" className="photox" aria-label="Close" onClick={() => setOpen(false)}>×</button>
            </div>
            <ol className="wirefeed">
              {feed.map((l) => (
                <li key={l.key}>
                  <button type="button" onClick={() => { setOpen(false); onGo(l.to[0], l.to[1]); }}>
                    <span className={`wirekind ${l.kind}`} aria-hidden="true" />
                    <span className="wiretext">{l.text}</span>
                    <span className="wirewhen">{wireWhen(l.at)}</span>
                  </button>
                </li>
              ))}
            </ol>
          </div>
        </>,
      )}
    </div>
  );
}

/** `11:02` for something that happened today, `Aug 30` for anything older. */
function wireWhen(iso: string): string {
  const d = new Date(iso);
  const today = new Date();
  const sameDay = d.getFullYear() === today.getFullYear()
    && d.getMonth() === today.getMonth()
    && d.getDate() === today.getDate();
  return sameDay
    ? d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false })
    : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

/* ------------------------------------------------------------------------- the one thing to do */

const toneOf = (u: Urgency) => (u === 'day' ? 'red' : u === 'week' ? 'amber' : u === 'passed' ? 'grey' : 'violet');

/**
 * Who can lift a refusal, read off the roster rather than written down.
 *
 * `SignIn.tsx` learned this the hard way: a hardcoded name is the sort of thing that stays wrong
 * for a year after the job changes hands. Falls back to a description rather than an empty
 * sentence, because "speak to " with nothing after it is worse than saying nothing.
 */
function commissionerNames(): string {
  const who = currentManagers()
    .filter((m) => m.role === 'commissioner' || m.role === 'admin')
    .map((m) => managerShort(m.id));
  if (who.length === 0) return 'a commissioner';
  if (who.length === 1) return who[0]!;
  return `${who.slice(0, -1).join(', ')} or ${who.at(-1)}`;
}

/**
 * The lead when there is one thing to do and a clock on it.
 *
 * THE ONLY TICKING NUMBER IN THE APP lives in this card's head, and only inside the last day. The
 * rail below echoes the same deadline at hour granularity and never ticks. That scarcity is the
 * point: a number that moves means something here because nothing else moves.
 *
 * A CLOSED DOOR IS NOT URGENT. Passed and locked drop the amber ground for a grey one, disable the
 * button, and name who can reopen it. Amber on a deadline nobody can meet any more is the app
 * shouting at somebody about something they cannot do.
 */
export function NextAction(
  { task, now, locked, lockedBy, onGo }: {
    task: HomeTask;
    now: number;
    locked: boolean;
    lockedBy?: string | null;
    onGo: (s: string, p: string) => void;
  },
) {
  const left = liveLeft(task.iso, now);
  const shut = !task.open;
  const ticking = Boolean(left?.ticking) && !shut;

  return (
    <section className={`card lead nextaction ${shut ? 'shut' : task.urgency}`}>
      <div className="leadhead">
        {ticking ? (
          <b className="leadclock">{left?.text}</b>
        ) : (
          <span className="leadeyebrow">
            {shut
              ? (locked ? 'LOCKED EARLY' : 'DEADLINE PASSED')
              : task.iso
                ? `NEXT ACTION · DUE IN ${left?.text ?? ''}`
                : 'NEXT ACTION · NO DATE SET'}
          </span>
        )}
        <div className="leadwhen">
          {ticking && <span className="leadeyebrow">DUE TODAY</span>}
          {task.iso
            ? <span>{shut ? `Closed ${bothZones(task.iso)}` : bothZones(task.iso)}</span>
            : <span>The commissioners have not announced a date</span>}
        </div>
      </div>
      <div className="leadbody">
        <h2>{task.title}</h2>
        <p>{task.body}</p>
        <button
          type="button"
          className={`btn ${task.done ? '' : 'primary'} leadcta`}
          disabled={shut}
          onClick={() => onGo(task.to[0], task.to[1])}
        >
          {shut ? closedLabel(task.key) : task.cta}
        </button>
        {shut && (
          <p className="refused">
            {locked
              ? `A commissioner locked ${thing(task.key)} early. ${commissionerNames()} can lift the lock.`
              : `${capital(thing(task.key))} closed at the deadline. ${commissionerNames()} can reopen yours.`}
            {locked && lockedBy ? ` Locked by ${managerShort(lockedBy)}.` : ''}
          </p>
        )}
        {task.cites.length > 0 && <Citations ids={task.cites} />}
      </div>
    </section>
  );
}

const thing = (key: string) => (key === 'keepers' ? 'declarations'
  : key === 'prefs' ? 'slot rankings'
    : key === 'proposal' ? 'rule proposals' : 'this');
const closedLabel = (key: string) => (key === 'keepers' ? 'Declarations closed'
  : key === 'prefs' ? 'Rankings closed'
    : key === 'proposal' ? 'Proposals closed' : 'Closed');
const capital = (t: string) => t.charAt(0).toUpperCase() + t.slice(1);

/* ------------------------------------------------------------- the calm lead: a ring and tiles */

/**
 * The lead when more than one thing is due and none of it is today.
 *
 * A RING, NOT A CLOCK. It is the share of the window that has elapsed, so it fills as the deadline
 * approaches and carries no seconds at all. The figure inside it is whole days, which is the unit
 * anybody uses when the answer is more than a day.
 */
export function DueRing(
  { days, count, iso }: { days: number; count: number; iso: string },
) {
  /* A window the app cannot measure gets an empty ring rather than a full or a guessed one. */
  const share = Math.max(0, Math.min(1, 1 - days / 30));
  return (
    <section className="card lead ringlead">
      <div
        className="ring"
        style={{ ['--fill' as string]: `${Math.round(share * 360)}deg` }}
        role="img"
        aria-label={`${days} days until ${count} things are due`}
      >
        <span className="ringnum">{days}</span>
        <span className="ringunit">days</span>
      </div>
      <div className="ringsays">
        <h2>{count} thing{count === 1 ? '' : 's'} due {shortDay(iso)}</h2>
        <p>{bothZones(iso)}</p>
      </div>
    </section>
  );
}

const shortDay = (iso: string) =>
  new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

/**
 * The calm state's tiles, sorted the way the task list is.
 *
 * THE FIRST TILE IS THE ONE THAT SHUTS FIRST and it alone carries the accent border and the filled
 * button. Two equal tiles would drop the ranking the whole task model exists to provide: "which of
 * these matters more" is an opinion that goes stale, "which shuts first" is a fact that re-sorts
 * itself.
 */
export function TaskTiles(
  { tasks, pips, onGo }: {
    tasks: readonly HomeTask[];
    /** Progress marks per task key, drawn instead of a sentence about how far along you are. */
    pips: Record<string, ReactNode>;
    onGo: (s: string, p: string) => void;
  },
) {
  if (tasks.length === 0) return null;
  return (
    <div className={`tiles${tasks.length === 1 ? ' one' : ''}`}>
      {tasks.map((t, i) => (
        <section key={t.key} className={`card tile${i === 0 ? ' first' : ''}`}>
          <span className="tileeyebrow">{tileState(t)}</span>
          <h3>{t.title}</h3>
          {pips[t.key]}
          <button
            type="button"
            className={`btn ${i === 0 ? 'primary' : ''} tilecta`}
            onClick={() => onGo(t.to[0], t.to[1])}
          >
            {t.done ? 'Review' : i === 0 ? 'Continue' : 'Start'}
          </button>
        </section>
      ))}
    </div>
  );
}

const tileState = (t: HomeTask) => {
  const what = t.key === 'keepers' ? 'SLATE' : t.key === 'prefs' ? 'SLOTS' : 'PROPOSAL';
  const how = t.done ? 'SUBMITTED' : 'NOT STARTED';
  return `${what} · ${how}`;
};

/* ------------------------------------------------------------------------------ the deadlines */

/**
 * The deadline rail. Its whole point is the ORDER.
 *
 * Sorted by urgency then by date, so the thing that shuts first is at the top without anybody
 * deciding which deadline matters more. Five rows, always the same five, and each says what it is
 * doing right now rather than making the reader work it out.
 *
 * A CLOSED ROW KEEPS A LINE. "When was that, and did it actually close?" is asked all August, and
 * a rail that loses a row a week reads as though something has gone missing. It drops to one dim
 * tick line carrying the fact it ended on.
 */
export function DueRail(
  { rows, now, title, commish, onGo }: {
    rows: readonly DeadlineRow[];
    now: number;
    title: string;
    /** Only a commissioner is offered the full table, because only he can open it. */
    commish: boolean;
    onGo: (s: string, p: string) => void;
  },
) {
  const open = rows.filter((r) => r.iso && r.urgency !== 'passed');
  const gone = rows.filter((r) => r.iso && r.urgency === 'passed');
  const unset = rows.filter((r) => !r.iso);

  return (
    <section className="card duerail">
      <div className="cardhead">
        <h3>{title}</h3>
        {commish && (
          <button type="button" className="linkbtn" onClick={() => onGo('settings', 'deadlines')}>
            All dates
          </button>
        )}
      </div>

      <ul className="duelist">
        {open.map((r) => (
          <li key={r.key}>
            <span className="what">{r.label}</span>
            <span className="fact">{r.fact}</span>
            <span className={`pill ${toneOf(r.urgency)}`}>{liveLeft(r.iso, now)?.text ?? ''}</span>
          </li>
        ))}
        {unset.map((r) => (
          <li key={r.key} className="unset">
            <span className="what">{r.label}</span>
            <span className="fact">The commissioners have not announced a date</span>
            <span className="pill dashed">not set</span>
          </li>
        ))}
      </ul>

      {gone.map((r) => (
        <p className="closedline" key={r.key}>
          <span aria-hidden="true">✓</span>
          {r.label} closed · {r.fact}
        </p>
      ))}
    </section>
  );
}

/* --------------------------------------------------------------------------- the commissioner */

/**
 * What the league still owes, and the one door into Admin.
 *
 * THE ADMIN ROW IS PINNED TO THE FOOT and it is the only way in. It was a tab on Home's sub-nav
 * until Home stopped having one, and before that a line in the sheet behind your own face — which
 * was reversed in August, because that sheet is where an app puts things about YOU and running the
 * league is not that. The card is the right host: it is already the surface about running the
 * league, it renders in every phase, and a manager never sees it.
 *
 * NO ROW WITHOUT A NUMBER. Every row is a count somebody can act on. Rows that would only restate
 * a date belong in the rail above, where every GM sees them too.
 */
export function CommishCard(
  { rows, chasing: waiting, onGo }: {
    rows: readonly TrackRow[];
    chasing: number;
    onGo: (s: string, p: string) => void;
  },
) {
  return (
    <section className="card commish">
      <div className="cardhead">
        <h3 className="ceyebrow">Commissioner</h3>
        {waiting > 0
          ? <span className="pill amber">{waiting} waiting</span>
          : <span className="mut">nothing waiting</span>}
      </div>
      <ul className="crows">
        {rows.map((r) => (
          <li key={r.key}>
            <button type="button" onClick={() => onGo(r.to[0], r.to[1])}>
              <span className="what">{r.label}</span>
              <b className="count">{r.count}</b>
              <span className={`note ${r.chase ? 'chase' : ''}`}>{r.note}</span>
              <span className="go" aria-hidden="true">›</span>
            </button>
          </li>
        ))}
        {rows.length === 0 && <li className="empty">Nothing to run right now.</li>}
      </ul>
      {/*
        THE ADMIN DOOR MOVED TO THE PROFILE MENU (Tim, 3 September). It was pinned here, at the
        foot of this card, and the accepted cost was that a commissioner wanted settings from a
        screen that was not Home and had to come back for them. The profile menu is in the
        header on every desktop screen and in the More sheet on every phone screen, so it is
        the same door with none of that cost. See `IdentityPanel`.
      */}
    </section>
  );
}

/* ---------------------------------------------------------------------------------- the viewer */

/**
 * Said once, at the top, and then out of the way.
 *
 * The failure to avoid is not a viewer breaking something — he cannot — it is him tapping a
 * control that quietly does nothing and concluding the app is broken. One line naming the state
 * beats a disabled button on every screen explaining itself.
 *
 * BLUE-GREY, NOT AMBER. This is a fact about who he is, not a warning about what is about to go
 * wrong, and amber in this app means a deadline.
 */
export function ViewerNote() {
  return (
    <p className="viewernote">
      You are watching. Every board and rule is readable; nothing here is yours to enter.
    </p>
  );
}

/* ------------------------------------------------------------------------------ small pieces */

/** A card that is a door and nothing else: a title, a fact, a chevron. */
export function DoorCard(
  { title, fact, onGo, quiet }: {
    title: string; fact: string; onGo: () => void; quiet?: boolean;
  },
) {
  return (
    <button type="button" className={`card door${quiet ? ' quiet' : ''}`} onClick={onGo}>
      <span className="doorwhat">
        <b>{title}</b>
        <span className="fact">{fact}</span>
      </span>
      <span className="go" aria-hidden="true">›</span>
    </button>
  );
}

/** The order, with your own row spliced in at its real rank when it falls outside the top. */
export function OrderCard(
  { shown, more, me, published, onGo }: {
    shown: ReadonlyArray<{ g: string; i: number }>;
    more: number;
    me: string;
    published: boolean;
    onGo: () => void;
  },
) {
  return (
    <section className="card order quiet">
      <div className="cardhead">
        <h3>{published ? 'Draft order' : 'Projected order'}</h3>
        <button type="button" className="linkbtn" onClick={onGo}>
          {more > 0 ? `+ ${more} more` : 'Full board'}
        </button>
      </div>
      <ol className="orderlist">
        {shown.map(({ g, i }) => (
          <li key={g} className={g.toUpperCase() === me ? 'mine' : ''}>
            <span className="slotno">{i + 1}</span>
            <GmBadge gm={g} />
            <b>{managerShort(g)}</b>
            {g.toUpperCase() === me && <span className="youpill">YOU</span>}
          </li>
        ))}
      </ol>
      {!published && (
        <p className="fact">Pending the lottery. The final order may be different.</p>
      )}
    </section>
  );
}
